import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Called after email verification succeeds — the setup token is issued by the backend's
// EmailVerificationController.verify() and passed to this page via ?token=... in the URL.
// POSTs to InitialPasswordSetupController POST /v1/users/password/set-initial.
// Intentionally mirrors ResetPasswordForm structure (same validation rules, same layout)
// but calls the set-initial endpoint instead of /reset, and on success directs the user
// to login rather than forgot-password.
type FormValues = {
  newPassword: string;
  confirmPassword: string;
};

const SetupPasswordForm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [setupComplete, setSetupComplete] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required(t('resource_3:isRequired', { field: t('resource_2:newPassword') }))
      .min(8, t('resource_message:passwordLength'))
      .matches(/^(?=.*[a-z])/, t('resource_message:passwordShouldContainUpparAndLowerCase'))
      .matches(/^(?=.*[A-Z])/, t('resource_message:passwordShouldContainUpparAndLowerCase'))
      .matches(/^(?=.*[0-9])/, t('resource_message:passwordShouldContainNo')),
    confirmPassword: Yup.string()
      .required(t('resource_3:isRequired', { field: t('resource_2:confirmPassword') }))
      .oneOf([Yup.ref('newPassword'), null], t('resource_message:passwordsMustMatch')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(
    async (payload) => {
      if (!token) {
        toast.error(t('resource_message:setupLinkMissingToken') as string);
        setTokenExpired(true);
        return;
      }
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_GATEWAY_HOST}/api/user-management/v1/users/password/set-initial`,
          { token, password: payload.newPassword }
        );

        toast.dark(response.data.responseMsg);
        if (response.data.responseCode === 'SUCCESS') {
          toast.success(t('resource_message:accountSetupCompleted') as string);
          setSetupComplete(true);
        }
      } catch (error: any) {
        const responseCode = error?.response?.data?.responseCode;
        const message = error?.response?.data?.responseMsg;

        if (responseCode === 'TOKEN_EXPIRED' || responseCode === 'INVALID_TOKEN') {
          setTokenExpired(true);
        }
        toast.error((message ?? t('resource_message:somethingWentWrong')) as string);
        console.error('Password setup error:', error);
      }
    },
    (error: any) => {
      console.error('Handle submit error: ', error);
    }
  );

  // Auto-redirect once setupComplete is true — gives the toast a moment to render
  // before the page navigates away.
  useEffect(() => {
    if (setupComplete) {
      const timer = setTimeout(() => {
        window.location.href = `${process.env.REACT_APP_CLIENT_APP_HOST}/app`;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [setupComplete]);

  if (setupComplete) {
    return (
      <div className='text-center fs--1'>
        <p>{t('resource_message:redirectingToLogin')}</p>
      </div>
    );
  }

  if (tokenExpired) {
    return (
      <div className='text-center fs--1'>
        <p>{t('resource_message:setupLinkInvalidOrExpired')}</p>
        <a href={`${process.env.REACT_APP_CLIENT_APP_HOST}/app`} className='link'>
          {t('resource_message:backToLogin')}
        </a>
      </div>
    );
  }

  return (
    <Form onSubmit={onSubmit}>
      <Form.Group className='mb-3'>
        <Form.Label>{t('resource_2:newPassword')}</Form.Label>
        <Form.Control
          placeholder={t('resource_2:newPassword')}
          {...register('newPassword')}
          className={`fs--1 inputBorder form-control ${
            errors.newPassword ? 'is-invalid' : ''
          }`}
          type='password'
        />
        <div className='invalid-feedback'>
          {errors.newPassword?.message?.toString()}
        </div>
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>{t('resource_2:confirmPassword')}</Form.Label>
        <Form.Control
          placeholder={t('resource_2:confirmPassword')}
          {...register('confirmPassword')}
          className={`fs--1 inputBorder form-control ${
            errors.confirmPassword ? 'is-invalid' : ''
          }`}
          type='password'
        />
        <div className='invalid-feedback'>
          {errors.confirmPassword?.message?.toString()}
        </div>
      </Form.Group>
      <div className='row'>
        <div className='d-flex justify-content-center align-items-center col-12'>
          <Form.Group className='text-center'>
            <Button type='submit' className='mt-2 w-100'>
              {t('resource_2:setupPassword')}
            </Button>
          </Form.Group>
        </div>
      </div>
    </Form>
  );
};

export default SetupPasswordForm;
